import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { Box, Divider, Typography } from "@mui/material";
import {
	IGallery,
	INVITATION_OCCASION,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../../store/services/invitations/dto/invitations.dto";
import { TemplateWrapper } from "./styles";
import AddIcon from "@mui/icons-material/Add";

import {
	flower01Url,
	flower02Url,
	flower03Url,
	flower04Url,
	flower05Url,
	flower06Url,
	flower08Url,
	flower07Url,
	envelope,
	paper,
	stamp,
	stem,
	loveStoryPic02
} from "./constants";
import { bismillah, LANG_AR, LANG_EN, RTL } from "../constants";
import MapComponent from "../Components/MapComponent/MapComponent";
import CountDown from "../CountDown";
import { getFieldMap } from "../../../../utils/inputs";
import InvitationGallery from "../../../InvitationGallery";
import langEN from "../lang.EN.json";
import langAR from "../lang.AR.json";
import SaveTheDateBtn from "../Components/SaveTheDateBtn";
import Comments from "../Components/Comments";
import { getWeddingDate } from "../../../../utils/formatDate";
import MapButton from "../Components/MapButton";
import "@fontsource/rochester";
import { TemplateColorObject } from "../../../../InvitationColorMatrix";
import PartnersSocialList from "../Components/PartnersSocialList";
import Footer from "../Components/Footer";

const Template01 = forwardRef(
	(
		{
			data,
			gallery,
			canEdit = false,
			colorMatrix
		}: {
			data: Invitations;
			gallery: IGallery[];
			colorMatrix: TemplateColorObject;
			canEdit?: boolean;
		},
		ref
	) => {
		const isTemplate = data?.type === INVITATION_TYPES.TEMPLATE;
		const commentsSectionRef = useRef<HTMLElement>(null);
		const locationSectionRef = useRef<HTMLElement>(null);

		useImperativeHandle(ref, () => ({
			scrollToComments: () => {
				commentsSectionRef.current && commentsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			},
			scrollToLocation: () => {
				locationSectionRef.current && locationSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}));

		const invitationId = data?._id || "";
		const lang = data?.language === LANG_EN ? langEN : langAR;
		const fieldMap = getFieldMap(invitationId);

		const invitation_type_title = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.the_engagement_of;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.the_katb_ktab_of;
				}
				default: {
					return lang.the_wedding_of;
				}
			}
		}, [data]);
		const invitation_location_header = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.engagement_location;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.katb_ktab_location;
				}
				default: {
					return lang.wedding_location;
				}
			}
		}, [data]);
		return (
			<>
				<TemplateWrapper id={"template_wrapper"} colormatrix={colorMatrix}>
					<Box className={"section_01"}>
						<Box className={"section_overlay"} />
						<Box
							className={`section_content ${!data?.has_images || (!canEdit && data?.fieldVisibility?.main_image?.hidden) ? "no_images" : ""}`}
						>
							<img
								className={"flower_01 pulse_animation"}
								id={data?.fieldVisibility?.main_image?.hidden ? "main_image" : ""}
								src={flower01Url}
								alt="flower"
							/>
							{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
								<Box mt={"14vh"}>
									<Box className={"main_frame_wrapper"}>
										<img
											className={`main_frame ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
											id={"main_image"}
											src={data?.main_image}
											alt="main couple"
											data-dispatch-id={fieldMap.MAIN_IMAGE}
										/>
										<img className={"main_frame_envelope"} src={envelope} alt="envelope" />
										<img className={"main_frame_paper"} src={paper} alt="paper" />
										<img className={"main_frame_stamp"} src={stamp} alt="stamp" />
									</Box>
								</Box>
							)}

							<img className={"flower_02 pulse_animation"} src={flower02Url} alt="flower" />
							<Box className={`save_date_wrapper ${data?.language === LANG_AR && RTL}`}>
								<Typography className={"wedding_of"} fontSize={data.language === LANG_AR ? "25px" : ""}>
									{invitation_type_title}
								</Typography>
								<Box display="flex" gap={1} alignItems={"center"} flexWrap={"wrap"} maxWidth={"270px"}>
									<Typography className={"partners_text guide_edit_field"} data-dispatch-id={fieldMap.GROOM_NAME}>
										{data?.groom_name}
									</Typography>
									<Typography className={"partners_text"}> {lang.and} </Typography>

									<Typography className={"partners_text"} data-dispatch-id={fieldMap.BRIDE_NAME}>
										{data?.bride_name}
									</Typography>
								</Box>

								<Typography className={"date"} data-dispatch-id={fieldMap.WEDDING_DATE}>
									{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate)}
								</Typography>

								{(canEdit || !data?.fieldVisibility?.save_the_date_text?.hidden) && (
									<Typography
										className={`info ${data?.fieldVisibility?.save_the_date_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
										data-dispatch-id={fieldMap.SAVE_THE_DATE}
									>
										{data?.save_the_date_text}
									</Typography>
								)}

								<SaveTheDateBtn canEdit={canEdit} data={data!} btnText={lang.save_the_date_button_text} />
							</Box>
							<div className="curve-container">
								<svg viewBox="0 0 100 20" preserveAspectRatio="none" className="curve">
									<path d="M0,20 Q50,10 100,20 L100,0 L0,0 Z" />
								</svg>
							</div>
						</Box>
					</Box>
					<Box className={"section_02"}>
						<Box className={"section_overlay"} />
						<Box className={"section_content"}>
							<div className="curve-container">
								<svg viewBox="0 0 100 20" preserveAspectRatio="none" className="curve">
									<path d="M0,20 Q50,10 100,20 L100,0 L0,0 Z" />
								</svg>
							</div>
							<img className={"flower_03 breeze_animation"} src={flower03Url} alt="flower" />
							{data?.is_islamic && <img className={"bismillah"} src={bismillah} alt="bismillah" />}
							<Box height={20} />
							{(canEdit || !data?.fieldVisibility?.intro_text?.hidden) && (
								<>
									<Typography
										className={`main_intro ${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
										data-dispatch-id={fieldMap.INTRO_TEXT}
									>
										{data?.intro_text}
									</Typography>
									<Box height={20} />
								</>
							)}
							{(canEdit || !data?.fieldVisibility?.sub_intro_text?.hidden) && (
								<>
									<Typography
										className={`main_sub_intro ${data?.fieldVisibility?.sub_intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
										data-dispatch-id={fieldMap.SUB_INTRO}
									>
										{data?.sub_intro_text}
									</Typography>

									<Box height={20} />
								</>
							)}

							<Box className={"count_down_wrapper"}>
								<Typography className={"date"} data-dispatch-id={fieldMap.WEDDING_DATE}>
									{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate)}
								</Typography>

								<CountDown
									data={data || ({} as Invitations)}
									daysString={lang.days}
									hoursString={lang.hours}
									minutesString={lang.mins}
									secondsString={lang.sec}
								/>
							</Box>
							<Box className={"partners_container"}>
								<Box className={"partner_01_wrapper"}>
									{data?.has_images && (canEdit || !data?.fieldVisibility?.groom_image?.hidden) && (
										<img
											className={`partner_img ${data?.fieldVisibility?.groom_image?.hidden ? "shaded" : ""}`}
											src={data?.groom_image}
											alt="groom"
											data-dispatch-id={fieldMap.GROOM_IMAGE}
										/>
									)}

									<Typography className={"animate-on-scroll delay-1"} data-dispatch-id={fieldMap.GROOM_NAME}>
										{data?.groom_name}
									</Typography>

									<PartnersSocialList
										socials={data?.partner_socials_1 || []}
										canEdit={canEdit}
										dispatchId={fieldMap.SOCIAL_MEDIA_1}
									/>
								</Box>
								{!data?.has_images && <img src={flower08Url} className={"partner_flower"} alt="flower" />}
								<Box className={"partner_02_wrapper"}>
									{data?.has_images && (canEdit || !data?.fieldVisibility?.bride_image?.hidden) && (
										<img
											className={`partner_img ${data?.fieldVisibility?.bride_image?.hidden ? "shaded" : ""}`}
											src={data?.bride_image}
											alt="bride"
											data-dispatch-id={fieldMap.BRIDE_IMAGE}
										/>
									)}

									<Typography className={"animate-on-scroll delay-2"} data-dispatch-id={fieldMap.BRIDE_NAME}>
										{data?.bride_name}
									</Typography>

									<PartnersSocialList
										socials={data?.partner_socials_2 || []}
										canEdit={canEdit}
										dispatchId={fieldMap.SOCIAL_MEDIA_2}
									/>
								</Box>
							</Box>
							<img src={flower04Url} className={"flower_04"} alt="flower" />
						</Box>
					</Box>
					{data?.is_islamic && (
						<Box className={"section_03"}>
							<Typography className={"quran_start"}>بِسْمِ اللهِ الرَّحْمَنِ الرَّحِيمِِ</Typography>
							<Typography className={"quran"}>
								﴿ وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ
								بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُون َ﴾
							</Typography>
							<Typography className={"quran_end"}>صَدَقَ اللهُ العَظِيمُ</Typography>
						</Box>
					)}
					{!data?.fieldVisibility?.loveStory?.hidden && (
						<Box className={"section_04"}>
							<Box className={"section_overlay"} />
							<Box className={"section_content"}>
								<img src={flower05Url} className={"flower_05 pulse_animation"} alt="flower" />
								<Typography className={"section_title"}>{lang.our_love_story}</Typography>
								<img src={stem} className={"stem"} alt="flower stem" />
								{data?.has_images && (canEdit || !data?.fieldVisibility?.story_image?.hidden) && (
									<Box className={`love_story_wrapper ${data?.fieldVisibility?.story_image?.hidden ? "shaded" : ""}`}>
										<img src={loveStoryPic02} className={"story_image_tag"} alt="love Story tag" />
										<img
											src={data?.story_image}
											className="story_image"
											alt="Love Story"
											data-dispatch-id={fieldMap.STORY_IMAGE}
										/>
									</Box>
								)}
								{data?.story.length > 0 && (
									<Box className={"stories"} data-dispatch-id={fieldMap.STORY}>
										{data?.story.map((story, index) => {
											return (
												<Box key={story._id} className={`story animate-on-scroll delay-${index + 1}`}>
													<Typography>{story.title}</Typography>
													<Divider />
													<Typography>{story.description}</Typography>
												</Box>
											);
										})}
									</Box>
								)}

								{canEdit && (data?.story || []).length === 0 && (
									<Box my={3} mb={7}>
										<Box className={"add_story"} data-dispatch-id={fieldMap.STORY}>
											<AddIcon />
										</Box>
									</Box>
								)}
								<img src={flower06Url} className={"flower_06 pulse_animation"} alt="flower" />

								<div className="curve-container">
									<svg viewBox="0 0 100 20" preserveAspectRatio="none" className="curve">
										<path d="M0,20 Q50,10 100,20 L100,0 L0,0 Z" />
									</svg>
								</div>
							</Box>
						</Box>
					)}
					{!data?.fieldVisibility?.gallery?.hidden && data?.has_images && (
						<Box className={"section_05"}>
							<Typography className={"section_title"}>{lang.our_gallery}</Typography>
							<img src={stem} className={"stem"} alt="flower stem" />
							<Box height={30}></Box>
							<Box data-dispatch-id={fieldMap.GALLERY}>
								<InvitationGallery gallery={gallery || []} canEdit={canEdit} />
							</Box>
						</Box>
					)}
					{!data?.fieldVisibility?.comments?.hidden && (
						<Box className={"section_06"} ref={commentsSectionRef}>
							<Box className={"section_overlay"} />
							<Box className={"section_content"}>
								{!data?.fieldVisibility?.gallery?.hidden && <Box height={30} />}
								<Typography className={"section_title"}>{lang.comments}</Typography>
								<img src={stem} className={"stem"} alt="flower stem" />
								<Box height={30}></Box>

								{(canEdit || !data?.fieldVisibility?.comments_text?.hidden) && (
									<>
										<Typography
											data-dispatch-id={fieldMap.COMMENTS_TEXT}
											className={`comment_text ${data?.fieldVisibility?.comments_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
										>
											{data?.comments_text}
										</Typography>

										<Box height={30}></Box>
									</>
								)}
								{(canEdit || !data?.fieldVisibility?.comments_sub_text?.hidden) && (
									<>
										<Typography
											data-dispatch-id={fieldMap.COMMENTS_SUB_TEXT}
											className={`comment_sub_text ${data?.fieldVisibility?.comments_sub_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
										>
											{data?.comments_sub_text}
										</Typography>

										<Box height={30}></Box>
									</>
								)}
								{(!data?.fieldVisibility?.comments_sub_text?.hidden ||
									!data?.fieldVisibility?.comments_text?.hidden) && (
									<img src={flower08Url} className={"flower_08"} alt="flower" />
								)}

								<Comments canEdit={canEdit} language={data?.language as LangType} colormatrix={colorMatrix} />
							</Box>
						</Box>
					)}
					<Box className={"section_08"} ref={locationSectionRef}>
						<Box className={"section_overlay"} />
						<Box className={"section_content"}>
							<img src={flower07Url} className={"flower_07 "} alt="flower" />
							{!data?.fieldVisibility?.location?.hidden && (
								<>
									<Typography className={"section_title"}>{invitation_location_header}</Typography>
									<img src={stem} className={"stem"} alt="flower stem" />
									<Box height={20} />
									{(canEdit || !data?.fieldVisibility?.wedding_location_text?.hidden) && (
										<Typography
											data-dispatch-id={fieldMap.LOCATION_TEXT}
											className={`location_text ${data?.fieldVisibility?.wedding_location_text?.hidden ? "shaded" : ""}`}
										>
											{data?.wedding_location_text}
										</Typography>
									)}

									<Box height={20} />
									<Box data-dispatch-id={fieldMap.MAP}>
										<MapComponent canEdit={canEdit} />
									</Box>
									<Box height={20} />
									<Box data-dispatch-id={fieldMap.MAP}>
										<MapButton canEdit={canEdit} text={lang.location} />
									</Box>
								</>
							)}

							<Box height={30} />
							{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
								<Box
									className={`partner_04_wrapper ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
									data-dispatch-id={fieldMap.MAIN_IMAGE}
								>
									<img src={data?.main_image} alt="couple" className={"partner_04"} />
								</Box>
							)}

							<Box
								my={3}
								display="flex"
								gap={1}
								alignItems={"center"}
								className={`${data?.language === LANG_AR && RTL} animate-on-scroll delay-1`}
								flexWrap={"wrap"}
							>
								<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partners_text"}>
									{data?.groom_name}
								</Typography>

								<Typography className={"partners_text"}> {lang.and} </Typography>
								<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partners_text"}>
									{data?.bride_name}
								</Typography>
							</Box>

							{(canEdit || !data?.fieldVisibility?.conclusion_text?.hidden) && (
								<Typography
									data-dispatch-id={fieldMap.CONCLUSION_TEXT}
									className={`conclusion_text ${data?.fieldVisibility?.conclusion_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.conclusion_text}
								</Typography>
							)}
						</Box>
					</Box>
					<Footer colormatrix={colorMatrix} />
				</TemplateWrapper>
			</>
		);
	}
);

export default Template01;
